import { createRouter, createWebHistory } from 'vue-router';
import { hasRoles, isAuthenticated } from '@/utils/auth';

// Lazy load all route components
const HomePage = () => import('@/pages/HomePage.vue');
const NotFound = () => import('@/pages/NotFound.vue');
const ForbiddenPage = () => import('@/pages/ForbiddenPage.vue');
const LoginPage = () => import('@/pages/LoginPage.vue');
const ClientsPage = () => import('@/pages/clients/ClientsPage.vue');
const AutoDisconnectedPage = () => import('@/pages/clients/AutoDisconnectedPage.vue');
const HeatMapPage = () => import('@/pages/clients/HeatMapPage.vue');
const MTActiveClientsPage = () => import('@/pages/clients/MTActiveClientsPage.vue');
const UserActionLogsPage = () => import('@/pages/logs/UserActionLogsPage.vue');
const ReceiptsPage = () => import('@/pages/transactions/ReceiptsPage.vue');
const RemittancePage = () => import('@/pages/transactions/RemittancePage.vue');
const CashflowPage = () => import('@/pages/users/CashflowPage.vue');
const UsersPage = () => import('@/pages/users/UsersPage.vue');

// Define constants for reusable values
const ALLOWED_ROLES = ['teller', 'tech', 'treasurer', 'admin', 'it'];
const DEFAULT_TITLE = 'Exodous Admin';

// Base route configuration to avoid repetition
const protectedRouteConfig = {
  requiresAuth: true,
  showHeader: true,
  roles: ALLOWED_ROLES
};

const routes = [
  // Handle duplicate / route by using redirect
  {
    path: '/',
    redirect: { name: 'dashboard' }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: HomePage,
    meta: protectedRouteConfig
  },
  // Clients routes
  {
    path: '/clients/all',
    name: 'clients-all',
    component: ClientsPage,
    meta: protectedRouteConfig
  },
  {
    path: '/clients/mt-active',
    name: 'clients-mt-active',
    component: MTActiveClientsPage,
    meta: protectedRouteConfig
  },
  {
    path: '/clients/heatmap',
    name: 'clients-heatmap',
    component: HeatMapPage,
    meta: protectedRouteConfig
  },
  {
    path: '/clients/autodc',
    name: 'clients-autodc',
    component: AutoDisconnectedPage,
    meta: protectedRouteConfig
  },
  // Users routes
  {
    path: '/users/list',
    name: 'users-list',
    component: UsersPage,
    meta: protectedRouteConfig
  },
  {
    path: '/users/cashflow',
    name: 'users-cashflow',
    component: CashflowPage,
    meta: protectedRouteConfig
  },
  // Transactions routes
  {
    path: '/transactions/receipts',
    name: 'transactions-receipts',
    component: ReceiptsPage,
    meta: protectedRouteConfig
  },
  {
    path: '/transactions/remittances',
    name: 'transactions-remittances',
    component: RemittancePage,
    meta: protectedRouteConfig
  },
  // System routes
  {
    path: '/system/user-action-logs',
    name: 'user-action-logs',
    component: UserActionLogsPage,
    meta: protectedRouteConfig
  },
  // Auth routes
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { requiresGuest: true } // Add this to handle guest-only routes
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: ForbiddenPage,
    meta: { showHeader: false }
  },
  // Catch-all route (always last)
  { 
    path: '/:catchAll(.*)', 
    name: 'not-found',
    component: NotFound, 
    meta: { showHeader: false } 
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return savedPosition || { top: 0, behavior: 'smooth' };
  }
});

// Optimized navigation guard
router.beforeEach((to, from, next) => {
  // Set page title
  document.title = to.meta.title || DEFAULT_TITLE;

  const isAuthed = isAuthenticated();

  // Handle authentication
  if (to.meta.requiresAuth && !isAuthed) {
    return next({ 
      name: 'login', 
      query: { redirect: to.fullPath } 
    });
  }

  // Handle guest-only routes (login page, etc.)
  if (to.meta.requiresGuest && isAuthed) {
    return next({ name: 'dashboard' });
  }

  // Handle role-based access control
  if (to.meta.roles && to.meta.roles.length > 0) {
    if (!hasRoles(to.meta.roles)) {
      return next({ name: 'forbidden' });
    }
  }

  next();
});

export default router;